import { FundingOrganismType } from "../content/registration/funding_organism";
import { SFObjectFieldType, SFOwnerFieldType, SFProductFieldType } from "../salesforce/fields";
import { UserType } from "../user/user";
import { ApprenticeshipEnterpriseType } from "./enterprise";

export type Student = {
    user_id: number;
    name: string;
    email: string;
    is_invited_to_event: boolean;
    session_id: number;
};

export interface IApprenticeshipPlanningList {
    id: number;
    training_days_count: number;
    sessions_count: number;
    starting_date: string;
    created_at: string;
    updated_at: string;
    product: string;
    parent_planning: {
        id: number;
        starting_date: string;
    } | null;
    first_training_day_date: string | null;
    duration: number;
}

export interface IApprenticeshipTrainingDayDetail {
    id: number;
    planning: IApprenticeshipPlanningList;
    number: number;
    date: string;
    display_date: string;
    active: boolean;
    override_id: number | null;
    created_at: Date;
    updated_at: Date;
}

export type INote = {
    id: number;
    note: string;
    draft: string | null;
    created_at: string;
    updated_at: string;
    draft_updated_at: string | null;
};

export type INoteSession = {
    lesson: {
        id: number;
        number: number;
        title: string;
        code: string;
    };
} & INote;

export type ISessionTrainingDay = {
    apprenticeship_training_day: IApprenticeshipTrainingDayDetail;
    notes: INoteSession[];
    has_viewed_replay: boolean;
    has_attended: boolean;
    replay_viewed_duration: number | null;
    id: number;
    formation_name: string;
    formation_duration: number | null;
    validated: boolean;
    session_time: number;
    is_asynchronous: boolean;
    event_datetime: string | null;
};
//& ISession

export type TrainingDay = {
    id: number;
    date: string;
    display_date: string;
    validated_count: number | null;
    total_count: number | null;
    has_sync: boolean;
};

export type PlanningStudent = {
    id: number;
    students: Student[];
    starting_date: string;
    created_at: string;
    updated_at: string;
    parent_planning: number;
    product: string;
};

export type ApprenticeshipRegistrationStatus =
    | "Pending"
    | "Created"
    | "Fulfilled"
    | "Submitted"
    | "NoShow"
    | "SearchingCompany"
    | "InterviewUpcoming"
    | "WaitingFeedback"
    | "CompanyFound"
    | "CompanyFulfilled"
    | "ContractToBeSigned"
    | "ContractSigned"
    | "Discarded"
    | "Abandonned"
    | "Terminated"
    | "Validated"
    | "Suspended";

export const ExamAcademy = [
    "Aix-Marseille",
    "Amiens",
    "Besançon",
    "Bordeaux",
    "Clermont-Ferrand",
    "Corse",
    "Créteil",
    "Dijon",
    "Grenoble",
    "Lille",
    "Limoges",
    "Lyon",
    "Montpellier",
    "Nancy-Metz",
    "Nantes",
    "Nice",
    "Normandie",
    "Orléans-Tours",
    "Paris",
    "Poitiers",
    "Reims",
    "Rennes",
    "Strasbourg",
    "Toulouse",
    "Versailles",
    "Guadeloupe",
    "Guyane",
    "Martinique",
    "La Réunion"
] as const;

export enum StudentTerminatedReason {
    does_not_meet_expectations = "Ne correspond pas aux attentes",
    does_not_follow_the_formation = "N’a pas suivi la formation",
    personal_or_family_problems = "Problèmes familiaux / personnels",
    health_problems = "Problème de santé",
    salary_problems = "Problème de salaire",
    relocation = "Déménagement",
    too_long_travel = "Trajet trop long",
    no_reason_to_abandon = "Abandon sans raison",
    misunderstanding = "Mésentente",
    other = "Autre (précisez en commentaire)"
}
export enum CompanyTerminatedReason {
    grave_fault = "Faute grave",
    mutual_agreement = "Accord commun",
    absenteeism_or_delay = "Absentéisme / Retard",
    not_meeting_expectations = "Pas à la hauteur des attentes (manque de motivation, manque de professionnalisme)",
    structure_closure = "Fermeture de la structure",
    team_disagreement = "Mésentente avec l'équipe",
    payment_problem = "Problème de paiement",
    relocation = "Déménagement",
    other = "Autre (précisez en commentaire)"
}
export enum TerminatedReasonCategory {
    first_45_days = "Rupture pendant les 45 premiers jours",
    mutual_agreement = "Rupture d'un commun accord",
    serious_misconduct = "Rupture pour faute grave/manquements répétés",
    unfitness = "Rupture pour inaptitude",
    diploma_obtained = "Rupture pour obtention du diplôme",
    admin_decision_health_risk = "Décision administrative (risque santé/intégrité)",
    prefect_decision_employer_breach = "Décision du Préfet (obligations employeur)"
}

/** Livret de suivi tuteur (CAP AEPE) : un créneau par semestre, URL présignée 15 min. */
export type TutorMonitoringFormType = {
    semester: 1 | 2 | 3;
    uploaded_at: string | null;
    download_url: string | null;
};

export type ApprenticeshipRegistrationType = {
    id: number;
    reference: string;
    apprenticeship_type: "Apprentissage" | "ContratPro" | "Pro-A";
    global_id: string;
    calendar_s3_url: string | null;
    calendar_enable_onboarding: boolean | null;
    calendar_enable_revision: boolean | null;
    school_day: number;
    submitted_at: string | null;
    validated_at: string | null;
    validated_by: UserType | null;
    discarded_at: string | null;
    discarded_by: UserType | null;
    discarded_reason: string;
    abandonned_at: string | null;
    abandonned_by: UserType | null;
    abandonned_reason: string;
    abandonned_reason_category: "already_found" | "change_of_project" | "search_deadline" | "other" | null;
    abandonned_reason_category_label?: string | null;
    abandonned_reason_categories?: { value: string; label: string }[];
    terminated_at: string | null;
    terminated_by: UserType | null;
    terminated_source: "company" | "student" | "school";
    terminated_reason: StudentTerminatedReason | CompanyTerminatedReason;
    terminated_reason_detail: string | null;
    terminated_supporting_documents: string[] | null;
    terminated_opco: boolean;
    terminated_opco_by: UserType | null;
    maintained: boolean;
    maintained_deadline: string | null;
    parent_registration: {
        id: number;
        company: {
            id: number;
            name: string;
            city: string;
            postal_code: string;
            sf_account?: number | null;
        } | null;
        cerfa_contract_ending_date: string | null;
        status: string;
    } | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    mobile_phone: string | null;
    nir: string | null;
    street: string | null;
    postal_code: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
    gender: string | null;
    nationality: string | null;
    birth_city: string | null;
    birth_date: string | null;
    birth_department: number | null;
    birth_country: string | null;
    is_born_abroad: boolean;
    has_apprenticeship_company: boolean;
    apprenticeship_company_name: string | null;
    apprenticeship_company_postal_code: string | null;
    apprenticeship_company_street: string | null;
    apprenticeship_company_city: string | null;
    apprenticeship_company_country: string | null;
    apprenticeship_company_responsable_first_name: string | null;
    apprenticeship_company_responsable_last_name: string | null;
    apprenticeship_company_responsable_email: string | null;
    apprenticeship_company_responsable_mobile_phone: string | null;

    cerfa_company_name: string | null;
    cerfa_immatriculation_place: string | null;
    cerfa_siret: string | null;
    cerfa_convention_number: string | null;
    cerfa_employer_type: string | null;
    cerfa_employer_specific: string | null;
    cerfa_naf: string | null;
    cerfa_total_employees: string | null;
    cerfa_signatory: string | null;
    cerfa_signatory_first_name: string | null;
    cerfa_signatory_last_name: string | null;
    cerfa_signatory_function: string | null;
    cerfa_opco: string | null;
    cerfa_company_phone: string | null;
    cerfa_company_email: string | null;
    cerfa_company_street: string | null;
    cerfa_company_postal_code: string | null;
    cerfa_company_city: string | null;
    cerfa_company_country: string | null;
    cerfa_tutor_first_name: string | null;
    cerfa_tutor_last_name: string | null;
    cerfa_tutor_birth_date: string | null;
    cerfa_tutor_email: string | null;
    cerfa_tutor_mobile_phone: string | null;
    cerfa_tutor_current_job: string | null;
    cerfa_tutor_highest_diploma: string | null;
    cerfa_tutor_highest_level_code: string | null;
    cerfa_contract_type: string | null;
    cerfa_derogation_type: string | null;
    cerfa_amendment_contract_number: string | null;
    cerfa_contract_starting_date: string | null;
    cerfa_formation_starting_date: string | null;
    cerfa_amendment_date: string | null;
    cerfa_contract_ending_date: string | null;
    cerfa_weekly_work_duration: string | null;
    cerfa_remuneration_first_year_starting_date: string | null;
    cerfa_remuneration_first_year_ending_date: string | null;
    cerfa_remuneration_first_year_percentage: string | null;
    cerfa_remuneration_first_year_type: string | null;
    cerfa_remuneration_first_year_gross_salary: string | null;

    cerfa_remuneration_first_year_starting_date_bis: string | null;
    cerfa_remuneration_first_year_ending_date_bis: string | null;
    cerfa_remuneration_first_year_percentage_bis: string | null;
    cerfa_remuneration_first_year_type_bis: string | null;

    cerfa_remuneration_second_year_starting_date: string | null;
    cerfa_remuneration_second_year_ending_date: string | null;
    cerfa_remuneration_second_year_percentage: string | null;
    cerfa_remuneration_second_year_type: string | null;
    cerfa_remuneration_second_year_starting_date_bis: string | null;
    cerfa_remuneration_second_year_ending_date_bis: string | null;
    cerfa_remuneration_second_year_percentage_bis: string | null;
    cerfa_remuneration_second_year_type_bis: string | null;

    cerfa_remuneration_third_year_starting_date: string | null;
    cerfa_remuneration_third_year_ending_date: string | null;
    cerfa_remuneration_third_year_percentage: string | null;
    cerfa_remuneration_third_year_type: string | null;

    cerfa_remuneration_third_year_starting_date_bis: string | null;
    cerfa_remuneration_third_year_ending_date_bis: string | null;
    cerfa_remuneration_third_year_percentage_bis: string | null;
    cerfa_remuneration_third_year_type_bis: string | null;
    cerfa_supplementary_pension_fund: string | null;
    cerfa_benefit_in_kind_food: string | null;
    cerfa_benefit_in_kind_accommodation: string | null;
    cerfa_particular_employer: boolean | null;
    cerfa_urssaf: string | null;
    cerfa_project_number: string | null;
    cerfa_job_title: string | null;
    cerfa_contract_nature: "cdi" | "cdd" | "temporary_work" | null;
    cerfa_prevention_organism: string | null;
    cerfa_classification: string | null;
    cerfa_level: string | null;
    cerfa_hierarchical_coefficient: string | null;
    cerfa_trial_period_duration: string | null;
    cerfa_conclusion_date: string | null;

    // Pro-A fields
    cerfa_pro_a_reconversion: boolean;
    cerfa_pro_a_vae: boolean;
    cerfa_pro_a_on_work_time: boolean;

    current_job_title: string | null;
    current_job_starting_date: string | null;
    current_job_level: string | null;
    current_job_classification: string | null;
    current_job_hierarchical_coefficient: string | null;

    social_minimum_type: string | null;

    pole_emploi_registration_number: string | null;
    pole_emploi_duration: string | null;

    is_former_apprenticeship: boolean;
    former_salary: string | null;
    former_weekly_work_duration: string | null;
    ok_to_work_saturday: boolean;

    passed_exam: boolean;
    exam_passed_date: string | null;
    exam_result: number | null;
    exam_attempts: number | null;
    exam_academy: (typeof ExamAcademy)[number] | null;

    hired_permanent_contract: boolean;
    permanent_contract_starting_date: string | null;
    permanent_contract_company_name: string | null;
    permanent_contract_company_postal_code: string | null;
    permanent_contract_salary: number | null;

    has_legal_representative: boolean;
    legal_representative_first_name: string | null;
    legal_representative_last_name: string | null;
    legal_representative_email: string | null;
    legal_representative_mobile_phone: string | null;
    legal_representative_birth_date: string | null;
    legal_representative_nationality: string | null;
    legal_representative_postal_code: string | null;
    legal_representative_street: string | null;
    legal_representative_city: string | null;
    legal_representative_country: string | null;
    want_to_move_out: boolean;
    move_out_city: string | null;
    move_out_postal_code: string | null;
    is_high_level_athlete: boolean;
    is_disabled_worker: boolean;
    is_registered_to_pole_emploi: boolean;
    want_to_create_enterprise: boolean;
    has_rqth_rights: boolean;
    aid_under_20: string | null;
    boe_beneficiary: string | null;
    social_regime: "unknown" | "msa" | "urssaf" | null;
    situation_before_contract:
        | "unknown"
        | "school"
        | "prepa_apprentissage"
        | "student"
        | "apprenticeship_contract"
        | "professionalization_contract"
        | "aided_contract"
        | "cfa_formation"
        | "cfa_formation_no_contract"
        | "other_situations"
        | "employee"
        | "job_seeker"
        | "inactive"
        | null;
    last_student_year_outcome: number | null;
    last_diploma_title: string | null;
    last_diploma_prepared: number | null;
    last_diploma: number | null;
    last_diploma_date: string | null;
    funding_organism: FundingOrganismType | null;
    opco_reference: string | null;
    opco_status: "Pending" | "Validated" | "Rejected" | null;
    organism_part: number | null;
    company_funding: number | null;
    company_funding_invoice_number: string | null;
    company_funding_invoice_url: string | null;
    company_funding_invoice_email: string | null;
    opco_funding: number | null;
    opco_funding_second_year: number | null;
    opco_funding_lock: boolean;
    opco_funding_custom: boolean;
    opco_submitted_date: string | null;
    coaching_walter_session_id: number | null;
    apprenticeship_walter_session_id: number | null;
    apprenticeship_session_starting_date: string | null;
    apprenticeship_session_ending_date: string | null;
    apprenticeship_session_length: number | null;
    docusign_envelope_id: string | null;
    docusign_created_at: string | null;
    docusign_sent_at: string | null;
    docusign_contract_url: string | null;
    docusign_event_history: DocusignEventHistoryType[];
    terminated_docusign_sent_at: string | null;
    terminated_docusign_created_at: string | null;
    terminated_docusign_contract_url: string | null;
    terminated_docusign_envelope_id: string | null;
    terminated_docusign_event_history: DocusignEventHistoryType[];
    terminated_docusign_signed_at: string | null;
    filled_cerfa: string | null;
    filled_convention: string | null;
    sf_product: SFProductFieldType | null;
    sf_opportunity_origin: number | null;
    sf_opportunity_candidate: number | null;
    sf_opportunity_apprenticeship: number | null;
    sf_case: SFObjectFieldType | null;
    csm: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        phone_number: string | null;
    } | null;
    webinaire_rentree_status: "present" | "absent" | "unknown";
    ob_eleve_done: boolean;
    ob_entreprise_done: boolean;
    ob_entreprise_auto_motif: "suivi_entreprise" | "appel_tuteur" | "doublon_tuteur" | null;
    company_suivi_submitted: boolean;
    tripartite_appointment_booked: boolean;
    tutor_monitoring_forms?: TutorMonitoringFormType[];
    sales_manager: SFOwnerFieldType | null;
    user_ce: SFOwnerFieldType | null;
    user_cap: SFOwnerFieldType | null;
    status: ApprenticeshipRegistrationStatus;
    session_status: "Validated" | "Ongoing" | "Late" | "" | null;
    student_state: "in_difficulty" | "average" | "performant" | null;
    company_state: "in_difficulty" | "average" | "performant" | null;
    cover_letter: string | null;
    curriculum_vitae: string | null;
    curriculum_vitae_highlight: string | null;
    driving_license: boolean;
    geographical_constraint: string | null;
    geographical_constraint_radius: number | null;
    language_mastery: string | null;
    language_mastery_score: number | null;
    commute_transportation: string | null;
    commute_max_duration: number | null;
    comment: string | null;
    desired_starting_date: string | null;
    priority: number;
    profile_qualities: string | null;
    created_at: string;
    updated_at: string;
    last_comment_at: string | null;
    last_call_at: string | null;
    public_updated_at: string | null;
    tasks_count: number;
    applications_count: number;
    interviews_count: number;
    future_interviews_count: number;
    past_interviews_count: number;
    company: ApprenticeshipEnterpriseType | null;
    company_id?: null | number;
    convention: string | null;
    booklet: string | null;
    admission_appointment_at: string | null;
    selection_grid: SelectionGrid | null;
    admission_grid: AdmissionGrid | null;
};

export type ApprenticeshipRegistrationMapType = {
    id: number;
    apprenticeship_type: "Apprentissage" | "ContratPro";
    company: {
        id: number;
        name: string;
        city: string;
        postal_code: string;
        sf_account?: number | null;
    } | null;
    first_name: string | null;
    last_name: string | null;
    birth_date: string | null;
    street: string | null;
    postal_code: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
    admission_appointment_at: string | null;
    sales_manager: SFOwnerFieldType | null;
    user_ce: SFOwnerFieldType | null;
    user_cap: SFOwnerFieldType | null;
    status: ApprenticeshipRegistrationStatus;
    created_at: string;
    updated_at: string;
    public_updated_at: string | null;
    cerfa_contract_starting_date: string | null;
    cerfa_contract_ending_date: string | null;
    commute_transportation: string | null;
    commute_max_duration: number | null;
    is_disabled_worker: boolean;
    priority: number;
    tasks_count: number;
    applications_count: number;
    interviews_count: number;
    future_interviews_count: number;
    past_interviews_count: number;
    want_to_move_out: boolean;
    move_out_city: string | null;
    move_out_postal_code: string | null;
    last_comment_at: string | null;
    last_call_at: string | null;
    category: "candidates_pending" | "candidates_searching" | "candidates_interview" | "candidates_suspended";
    /** Preferred SF opportunity pk for CRM deep-links when standard-offers is on. */
    sf_opportunity_id?: number | null;

    selection_grid: {
        created_at: string;
        updated_at: string;
        questions: {
            question: string;
            score: number;
        }[];
        final_score: number;
    } | null;
    admission_grid: {
        created_at: string;
        updated_at: string;
        questions: {
            question: string;
            score: number;
        }[];
        final_score: number;
    } | null;
};

export type ApprenticeshipPicklistType = {
    diplomas: DiplomaType[];
    last_student_year_outcomes: LastStudentYearOutcomeType[];
    funding_organisms: FundingOrganismType[];
};

export type DiplomaType = {
    id: number;
    code: string;
    name: string;
    tooltip: string;
};

export type LastStudentYearOutcomeType = {
    id: number;
    code: string;
    name: string;
    tooltip: string;
};

export type ApprenticeshipRegistrationLightType = {
    id: number;
    priority: number;
    apprenticeship_type: "Apprentissage" | "ContratPro";
    global_id: string;
    first_name: string;
    last_name: string;
    email: string;
    birth_date: string;
    want_to_move_out: boolean;
    move_out_city: string;
    city: string;
    last_call_at: string;
    status: ApprenticeshipRegistrationStatus;
    sf_product: SFProductFieldType | null;
    move_out_postal_code: string | null;
    desired_starting_date: string | null;
    postal_code: string | null;
    interviews_count: number;
    user_cap: SFOwnerFieldType | null;
    application_status: "interview_scheduled" | "interview_passed" | "interested" | "resume_submitted" | "new";
    matched_criteria?: Array<"contract" | "rqth" | "age">;
    curriculum_vitae: string | null;
    status_updated_at_annotated: string | null;
};

export type SelectionGridQuestion = {
    id: number;
    question: string;
    score: number | null;
};

export type SelectionGrid = {
    created_at: string;
    updated_at: string;
    questions: SelectionGridQuestion[];
    final_score: number;
    completed_by: string | null;
};

export type AdmissionGridQuestion = {
    id: number;
    question: string;
    score: number | null;
};

export type AdmissionGrid = {
    created_at: string;
    updated_at: string;
    questions: AdmissionGridQuestion[];
    final_score: number;
    completed_by: string | null;
};

export type DocusignEventHistoryType = {
    sender: {
        email: string;
        userId: string;
        userName: string;
        accountId: string;
        ipAddress: string;
    };
    status: string;
    anySigner: any;
    emailBlurb: string;
    envelopeId: string;
    purgeState: string;
    recipients: {
        seals: Array<any>;
        agents: Array<any>;
        editors: Array<any>;
        signers: Array<{
            name: string;
            email: string;
            status: string;
            userId: string;
            lastName: string;
            firstName: string;
            recipientId: string;
            routingOrder: string;
            sentDateTime: string;
            recipientType: string;
            completedCount: string;
            creationReason: string;
            deliveryMethod: string;
            isBulkRecipient: string;
            recipientIdGuid: string;
            requireIdLookup: string;
            deliveredDateTime?: string;
            requireUploadSignature: string;
            signedDateTime?: string;
        }>;
        notaries: Array<any>;
        witnesses: Array<any>;
        carbonCopies: Array<{
            name: string;
            email: string;
            status: string;
            userId: string;
            lastName: string;
            firstName: string;
            recipientId: string;
            routingOrder: string;
            sentDateTime: string;
            recipientType: string;
            completedCount: string;
            deliveryMethod: string;
            recipientIdGuid: string;
            requireIdLookup: string;
        }>;
        intermediaries: Array<any>;
        recipientCount: string;
        inPersonSigners: Array<any>;
        certifiedDeliveries: Array<any>;
        currentRoutingOrder: string;
    };
    allowMarkup: string;
    envelopeUri: string;
    expireAfter: string;
    documentsUri: string;
    emailSubject: string;
    sentDateTime: string;
    templatesUri: string;
    allowReassign: string;
    enableWetSign: string;
    expireEnabled: string;
    is21CFRPart11: string;
    recipientsUri: string;
    attachmentsUri: string;
    autoNavigation: string;
    certificateUri: string;
    expireDateTime: string;
    createdDateTime: string;
    customFieldsUri: string;
    notificationUri: string;
    signingLocation: string;
    allowViewHistory: string;
    envelopeLocation: string;
    envelopeMetadata: {
        allowCorrect: string;
        allowAdvancedCorrect: string;
        enableSignWithNotary: string;
    };
    isDynamicEnvelope: string;
    burnDefaultTabData: string;
    envelopeIdStamping: string;
    hasFormDataChanged: string;
    initialSentDateTime: string;
    documentsCombinedUri: string;
    lastModifiedDateTime: string;
    signerCanSignOnMobile: string;
    statusChangedDateTime: string;
    disableResponsiveDocument: string;
    isSignatureProviderEnvelope: string;
};

export type ApprenticeshipRegistrationRelatedType = {
    id: number;
    created_at: string;
    updated_at: string;
    status: ApprenticeshipRegistrationStatus;
};
